import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useUserCompanies, UserCompany } from "@/hooks/useUserCompanies";
import { useAuthSession } from "@/hooks/useAuthSession";

interface ActiveCompanyContextType {
  activeCompany: UserCompany | null;
  setActiveCompany: (company: UserCompany) => void;
  companies: UserCompany[];
  pendingCompanies: UserCompany[];
  isLoading: boolean;
  hasMultipleCompanies: boolean;
  hasPendingVerification: boolean;
}

const ActiveCompanyContext = createContext<ActiveCompanyContextType | undefined>(undefined);

const ACTIVE_COMPANY_KEY = "activeCompanyId";

export function ActiveCompanyProvider({ children }: { children: ReactNode }) {
  const { data: session } = useAuthSession(); // Use cached session
  const user = session?.user ?? null;
  const { data: companiesData, isLoading } = useUserCompanies(user?.id || null, true);
  const companies = companiesData?.verified || [];
  const pendingCompanies = companiesData?.pending || [];
  const [activeCompany, setActiveCompanyState] = useState<UserCompany | null>(null);

  // Initialize active company from localStorage or auto-select first
  useEffect(() => {
    if (isLoading || companies.length === 0) return;

    const savedCompanyId = localStorage.getItem(ACTIVE_COMPANY_KEY);
    
    if (savedCompanyId) {
      // Try to restore saved company
      const savedCompany = companies.find((c) => c.company.id === savedCompanyId);
      if (savedCompany) {
        setActiveCompanyState(savedCompany);
        return;
      }
    }

    // Auto-select first company if none selected or saved company not found
    setActiveCompanyState(companies[0]);
  }, [companies, isLoading]);

  const setActiveCompany = (company: UserCompany) => {
    setActiveCompanyState(company);
    localStorage.setItem(ACTIVE_COMPANY_KEY, company.company.id);
  };

  const value = React.useMemo<ActiveCompanyContextType>(
    () => ({
      activeCompany,
      setActiveCompany,
      companies,
      pendingCompanies,
      isLoading,
      hasMultipleCompanies: companies.length > 1,
      hasPendingVerification: pendingCompanies.length > 0,
    }),
    [activeCompany, companies, pendingCompanies, isLoading]
  );

  return (
    <ActiveCompanyContext.Provider value={value}>
      {children}
    </ActiveCompanyContext.Provider>
  );
}

export function useActiveCompany() {
  const context = useContext(ActiveCompanyContext);
  if (context === undefined) {
    throw new Error("useActiveCompany must be used within ActiveCompanyProvider");
  }
  return context;
}
